<script setup>
import { storeToRefs } from 'pinia';
import { useModal } from 'vue-final-modal';
import BiMockDataConfirmPopup from '~/bi/components/query-builder/bi-mock-data-confirm-popup.vue';
import { useBiDataFormatter } from '~/bi/components/widgets/table-widget/composables/bi-table-data-formatter.composable.js';
import { useBIQueryBuilder } from '~/bi/composables/useBIQueryBuilder.js';
import { BI_ACTUAL_CHARTS } from '~/bi/constants/bi-constants';
import { useBiStore } from '~/bi/store/bi.store';
import { generateMockData } from '~/bi/utils/bi-mock-data-generator.js';
import { useCommonImports } from '~/common/composables/common-imports.composable';
import { sleep } from '~/common/utils/common.utils';
import { useBiChartBuilderHelpers } from '../composables/bi-chart-builder-helpers.composable';

const props = defineProps({
  mode: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
  view: {
    type: String,
    default: 'data-builder',
    validator: value => ['data-builder', 'chart-builder'].includes(value),
  },
  widgetName: {
    type: String,
    default: '',
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  widgetBuilderData: {
    type: Array,
    default: () => [],
  },
  widgetBuilderMetadata: {
    type: Object,
    default: () => ({}),
  },
  widgetSqlQuery: {
    type: String,
    default: '',
  },
  widgetId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close']);

const bi_store = useBiStore();
const { widget_builder_config, all_schemas, widget_builder_data, widget_builder_metadata } = storeToRefs(bi_store);

const { $t } = useCommonImports();

const { createDefaultSelection } = useBIQueryBuilder();
const { parseConfigToFormData } = useBiChartBuilderHelpers();

const state = reactive({
  view: 'data-builder',
  is_table_selected: false,
  is_loading: false,
  widget_name: 'Untitled widget',
  is_renaming: false,
  is_using_mock_data: false,
});

async function saveWidget(chart_config) {
  logger.log('SAVE WIDGET', widget_builder_data.value, chart_config, widget_builder_config.value);
  if (props.mode === 'create') {
    const uid = crypto.randomUUID();

    bi_store.createWidget(
      uid,
      {
        name: state.widget_name,
        config: {
          query: widget_builder_config.value.query,
          chart: chart_config,
          quick_filters: widget_builder_config.value.quick_filters,
        },
      },
      { w: 10, h: 10 },
    );

    emit('close');
    await sleep(500);
    const el = document.querySelectorAll(`[uid='${uid}']`)?.[0];
    el?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }
  else {
    bi_store.updateWidget(props.widgetId, {
      name: state.widget_name,
      config: {
        query: widget_builder_config.value.query,
        chart: chart_config,
        quick_filters: widget_builder_config.value.quick_filters,
      },
    });
    emit('close');
  }
}

async function selectTable(table) {
  state.is_loading = true;
  await bi_store.selectTable(table);
  widget_builder_config.value.query.stages.push({
    table: table.name,
    ...createDefaultSelection(),
  });
  state.is_table_selected = true;
  state.is_loading = false;
}

function selectSeries(table) {
  widget_builder_config.value.query.stages.push({
    table,
    ...createDefaultSelection(),
  });
  state.is_table_selected = true;
}

function handleNameChange(name) {
  state.widget_name = name;
  state.is_renaming = false;
}

function closeEditor() {
  if (state.widget_name)
    state.is_renaming = false;
}

function onContinueToChartBuilder(payload) {
  state.is_using_mock_data = !!payload?.is_mock_data;
  state.view = 'chart-builder';
}

function onGoBackToDataBuilder() {
  if (state.is_using_mock_data) {
    widget_builder_data.value = [];
    widget_builder_metadata.value = {};
    state.is_using_mock_data = false;
  }
  state.view = 'data-builder';
}

function getAllTableNames(stages) {
  const tables = [stages[0].table];
  stages.forEach((stage) => {
    stage.joins?.forEach((join) => {
      if (join.table.name)
        tables.push(join.table.name);
    });
  });
  return tables;
}

const { open: openMockDataConfirm, close: closeMockDataConfirm } = useModal({
  component: BiMockDataConfirmPopup,
  attrs: {
    onClose() {
      closeMockDataConfirm();
    },
    onConfirm() {
      const stages = widget_builder_config.value.query.stages;
      const { data, metadata } = generateMockData(stages);
      const columns_mapping = bi_store.alias_to_column_mapping();
      const { formatWidgetData } = useBiDataFormatter();
      widget_builder_data.value = formatWidgetData(data, metadata, columns_mapping);
      widget_builder_metadata.value = metadata;
      state.is_using_mock_data = true;
      state.view = 'chart-builder';
      closeMockDataConfirm();
    },
  },
});

onMounted(async () => {
  state.is_loading = true;
  bi_store.reset_widget_builder();
  if (props.mode === 'edit') {
    widget_builder_config.value = props.config;
    widget_builder_config.value.quick_filters = (widget_builder_config.value.quick_filters || []).map(f => ({ ...f, applied_value: null }));
    const table_names = getAllTableNames(widget_builder_config.value.query.stages);
    const promises = table_names.map((table_name, index) => bi_store.getTableColumns({ table_name, prefix_alias: index !== 0 }));
    const { data, metadata } = await bi_store.getWidgetData(widget_builder_config.value.query.stages, 'preview', null, props.config.quick_filters);
    await Promise.all(promises);
    state.is_table_selected = true;
    state.widget_name = props.widgetName;
    widget_builder_data.value = data;
    widget_builder_metadata.value = metadata;
    if (BI_ACTUAL_CHARTS.map(chart => chart.replace('_chart', '')).includes(props.config.chart.type)) {
      widget_builder_config.value.chart = parseConfigToFormData(props.config.chart).chart;
    }
    else {
      widget_builder_config.value.chart = props.config.chart;
    }
    if (props.view === 'chart-builder') {
      if (data?.length) {
        state.view = 'chart-builder';
      }
      else {
        openMockDataConfirm();
      }
    }
  }
  else {
    bi_store.sql_query = '';
    widget_builder_config.value.quick_filters = [];
  }
  state.is_loading = false;
});
</script>

<template>
  <BiChartBuilderLayout @close="emit('close')">
    <template v-if="!state.is_table_selected">
      <div class="col-span-12">
        <HawkModalHeader @close="emit('close')">
          <template #title>
            {{ $t('Choose a dataset') }}
          </template>
        </HawkModalHeader>
        <HawkModalContent class="!p-0 max-h-[calc(100vh-85px)] overflow-auto">
          <div v-if="state.is_loading" class="text-sm font-medium text-gray-700 mb-2">
            <HawkLoader />
          </div>
          <bi-dataset-picker v-else :all-schemas="all_schemas" @selected="selectTable" @selected-series="selectSeries" />
        </HawkModalContent>
      </div>
    </template>
    <template #title>
      <div class="font-semibold text-lg h-9">
        <HawkEditableName
          v-if="state.is_renaming"
          class="max-w-[50vw]"
          :name="state.widget_name"
          :placeholder="$t('Enter name of the widget')"
          :input_classes="{
            TextElement: {
              input: 'font-semibold !text-xl !p-0',
              inputContainer: 'border-0 border-b',
            },
            ElementLayout: {
              innerWrapper: 'border-b',
            },
          }"
          @close="closeEditor"
          @update="handleNameChange($event)"
        />
        <div v-else class="group flex items-center gap-3 text-xl text-gray-900 font-semibold max-w-[65vw] mt-[3px]" @click="state.is_renaming = true">
          <span>
            {{ state.widget_name }}
          </span>
          <IconHawkEditOne class="w-4 h-4 group-hover:visible invisible cursor-pointer" @click="state.is_renaming = true" />
        </div>
      </div>
    </template>
    <!-- Left side -->
    <template #left-content>
      <BiQueryBuilder v-if="state.view === 'data-builder'" />
      <BiChartBuilder v-else-if="state.view === 'chart-builder'" @go-back="onGoBackToDataBuilder" />
    </template>
    <!-- Right side -->
    <template #right-content>
      <BiQueryBuilderDataPreview
        v-if="state.view === 'data-builder'"
        @continue="onContinueToChartBuilder"
      />
      <BiWidgetPreview
        v-else-if="state.view === 'chart-builder'"
        :widget-name="state.widget_name"
        @continue="saveWidget"
      />
    </template>
  </BiChartBuilderLayout>
</template>
